import Heading from '@/common/shared/heading'
import { Separator } from '@/common/ui/separator'
import PlayerGameStatsTable from '../PlayerGameStatsTable'
import { DataTableSearch } from '@/common/ui/table/data-table-search'
import DateRangeButtons from '@/common/components/DateRangeButtons'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import { DataTableResetFilter } from '@/common/ui/table/data-table-reset-filter'
import type { components } from '@/schema'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { parseAsString, useQueryState } from 'nuqs'

type PlayerGameStatsItem = components['schemas']['PlayerGameStatsItemResponse']

interface PlayerGameStatsListingProps {
	data: PlayerGameStatsItem[]
	total: number
	isLoading?: boolean
	providerOptions: Array<{ value: string; label: string }>
	sortByOptions: Array<{ value: string; label: string }>
}

export default function PlayerGameStatsListing({
	data,
	total,
	isLoading,
	providerOptions,
	sortByOptions,
}: PlayerGameStatsListingProps) {
	const [provider, setProvider] = useQueryState(
		'provider',
		parseAsString.withOptions({ shallow: false }).withDefault(''),
	)

	const [sortBy, setSortBy] = useQueryState(
		'sortBy',
		parseAsString.withOptions({ shallow: false }).withDefault('totalPlayCount'),
	)

	const [sortOrder, setSortOrder] = useQueryState(
		'sortOrder',
		parseAsString.withOptions({ shallow: false }).withDefault('desc'),
	)

	const allProviderOptions = [
		{ label: 'ทั้งหมด', value: '' },
		...providerOptions,
	]

	const sortOrderOptions = [
		{ label: 'มากไปน้อย', value: 'desc' },
		{ label: 'น้อยไปมาก', value: 'asc' },
	]

	return (
		<div className="space-y-4">
			<div className="flex justify-between">
				<Heading
					title="สถิติการเล่นเกมของผู้เล่น"
					description="สถิติรวมการเล่นเกมของผู้เล่นทั้งหมด แสดงข้อมูลเกมที่เล่น ยอดเดิมพัน และกำไร/ขาดทุน"
				/>

				<div className="flex flex-col items-end gap-4">
					<DateRangePicker />
					<DateRangeButtons />
				</div>
			</div>

			<div className="flex flex-wrap items-center gap-2">
				<DataTableSearch placeHolder="ค้นหา..." />

				<Select value={provider} onValueChange={(value) => setProvider(value)}>
					<SelectTrigger className="w-full sm:w-48">
						<SelectValue placeholder="เลือกผู้ให้บริการ" />
					</SelectTrigger>
					<SelectContent>
						<SelectGroup>
							{allProviderOptions.map((option) => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectGroup>
					</SelectContent>
				</Select>

				<Select value={sortBy} onValueChange={(value) => setSortBy(value)}>
					<SelectTrigger className="w-full sm:w-48">
						<SelectValue placeholder="เรียงตาม" />
					</SelectTrigger>
					<SelectContent>
						<SelectGroup>
							{sortByOptions.map((option) => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectGroup>
					</SelectContent>
				</Select>

				<Select
					value={sortOrder}
					onValueChange={(value) => setSortOrder(value)}
				>
					<SelectTrigger className="w-full sm:w-40">
						<SelectValue placeholder="เรียงลำดับ" />
					</SelectTrigger>
					<SelectContent>
						<SelectGroup>
							{sortOrderOptions.map((option) => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectGroup>
					</SelectContent>
				</Select>

				<DataTableResetFilter />
			</div>

			<Separator />
			<PlayerGameStatsTable data={data} total={total} isLoading={isLoading} />
		</div>
	)
}
